/**
 * ITC-04 — quarterly job-work challan report (garment Increment 10, M11).
 *
 * Lists job-worker (`is_job_worker`) vendor bills for an FY quarter with a CSV
 * download, plus the LUT / AEPC RCMC expiry hygiene banner. V1 is the report
 * only — §143 challan generation + auto-filing are V2.
 *
 * @see prototype/compliance — compliance surfaces baseline
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarClock, Download, ShieldAlert } from "lucide-react";
import { Alert, Button, StatusPill, toast } from "@flint/ui";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { trpc } from "../../../../lib/trpc.ts";
import { paiseToInr } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/compliance/itc04/")({
  component: Itc04Route,
});

/** Current Indian FY + quarter from today (FY starts April). */
function currentFyQuarter(): { fy: number; quarter: number } {
  const now = new Date();
  const m = now.getMonth(); // 0 = Jan
  const fy = m >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const quarter = m >= 3 && m <= 5 ? 1 : m >= 6 && m <= 8 ? 2 : m >= 9 ? 3 : 4;
  return { fy, quarter };
}

function Itc04Route() {
  const init = currentFyQuarter();
  const [fy, setFy] = useState(init.fy);
  const [quarter, setQuarter] = useState(init.quarter);
  const utils = trpc.useUtils() as unknown as Record<string, any>;

  const report = trpc.itc04.getQuarter.useQuery({ fy, quarter });
  const deadlines = trpc.deadlines.list.useQuery({});

  async function downloadCsv() {
    try {
      const res = (await utils.itc04.exportCsv.fetch({ fy, quarter })) as { fileName: string; csv: string };
      const url = URL.createObjectURL(new Blob([res.csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = res.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ITC-04 CSV downloaded", { description: res.fileName });
    } catch (err) {
      toast.error("Couldn't export ITC-04", { description: (err as { message?: string }).message ?? "Unknown error" });
    }
  }

  const data = report.data;

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Compliance", to: "/" }, { label: "ITC-04" }]}>
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-ink">ITC-04 · job-work challans</h1>
            <p className="mt-0.5 text-sm text-muted">Goods to / from job-workers for the quarter. V1 is the report — filing is manual.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="rounded-sm border border-line px-2 py-1 text-sm text-ink-2 hover:bg-bg" onClick={() => setFy((y) => y - 1)}>‹</button>
            <span className="font-mono text-sm font-semibold text-ink">FY {fy}-{String((fy + 1) % 100).padStart(2, "0")}</span>
            <button type="button" className="rounded-sm border border-line px-2 py-1 text-sm text-ink-2 hover:bg-bg" onClick={() => setFy((y) => y + 1)}>›</button>
            <div className="ml-2 flex gap-1">
              {[1, 2, 3, 4].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuarter(q)}
                  className={`rounded-sm px-2.5 py-1 text-sm font-medium ${quarter === q ? "bg-ink text-paper" : "border border-line text-ink-2 hover:bg-bg"}`}
                >
                  Q{q}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* LUT / RCMC expiry hygiene */}
        {deadlines.data && deadlines.data.warnings.length > 0
          ? deadlines.data.warnings.map((w) => (
              <Alert key={w.credential} intent={w.status === "expired" ? "bad" : "warn"}>
                <span className="inline-flex items-center gap-1.5">
                  <ShieldAlert size={14} />
                  {w.label} {w.status === "expired" ? `expired ${Math.abs(w.daysToExpiry)} days ago` : `expires in ${w.daysToExpiry} days`} ({w.expiryDate}).
                </span>
              </Alert>
            ))
          : null}

        {report.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : report.error ? (
          <Alert intent="bad">Couldn't load ITC-04. {report.error.message}</Alert>
        ) : !data ? null : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill tone="sky" size="sm" dot>{data.challanCount} challan{data.challanCount === 1 ? "" : "s"}</StatusPill>
              <StatusPill tone="muted" size="sm" dot>{data.jobWorkerCount} job worker{data.jobWorkerCount === 1 ? "" : "s"}</StatusPill>
              <span className="text-xs text-muted">{data.periodStart} → {data.periodEnd}</span>
              <Button size="sm" intent="ghost" disabled={data.challanCount === 0} onClick={() => void downloadCsv()} className="ml-auto">
                <Download size={14} /> Download CSV
              </Button>
            </div>

            {data.challanCount === 0 ? (
              <div className="rounded-md border border-dashed border-line bg-bg px-4 py-10 text-center">
                <CalendarClock className="mx-auto mb-2 text-muted" size={22} />
                <p className="text-sm font-medium text-ink-2">No job-work challans this quarter</p>
                <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
                  Bills from vendors flagged as job-workers in this window will appear here for the ITC-04 return.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-line bg-paper">
                <table className="w-full min-w-[640px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                      <th className="px-3 py-2 text-left font-medium">Job worker</th>
                      <th className="px-3 py-2 text-left font-medium">GSTIN</th>
                      <th className="px-3 py-2 text-left font-medium">Challan / bill</th>
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2 text-right font-medium">Taxable</th>
                      <th className="px-3 py-2 text-right font-medium">Tax</th>
                      <th className="px-3 py-2 text-right font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={i} className="border-b border-line">
                        <td className="px-3 py-2 text-ink">{r.vendorName}</td>
                        <td className="px-3 py-2 font-mono text-xs text-ink-2">{r.vendorGstin ?? "—"}</td>
                        <td className="px-3 py-2 text-ink-2">{r.docNumber ?? "—"}</td>
                        <td className="px-3 py-2 font-mono text-ink-2">{r.date}</td>
                        <td className="px-3 py-2 text-right font-mono text-ink-2">{paiseToInr(r.taxablePaise)}</td>
                        <td className="px-3 py-2 text-right font-mono text-ink-2">{paiseToInr(r.taxPaise)}</td>
                        <td className="px-3 py-2 text-right font-mono text-ink">{paiseToInr(r.totalPaise)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-line font-semibold">
                      <td className="px-3 py-2 text-ink" colSpan={4}>Total</td>
                      <td className="px-3 py-2 text-right font-mono text-ink">{paiseToInr(data.totalTaxablePaise)}</td>
                      <td className="px-3 py-2 text-right font-mono text-ink">{paiseToInr(data.totalTaxPaise)}</td>
                      <td className="px-3 py-2 text-right font-mono text-ink">{paiseToInr(data.totalPaise)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </WorkspaceShell>
  );
}
